import { useState } from "react";
import {
  List,
  Datagrid,
  TextField,
  TextInput,
  NumberField,
  BooleanField,
  Edit,
  Create,
  SimpleForm,
  NumberInput,
  BooleanInput,
  ReferenceInput,
  ReferenceField,
  SelectInput,
  FunctionField,
  TopToolbar,
  CreateButton,
  ExportButton,
  useNotify,
  useRefresh,
  required,
} from "react-admin";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const rosterFilters = [
  <ReferenceInput key="season_id" source="season_id" reference="admin/seasons" alwaysOn>
    <SelectInput label="Stagione" optionText="label" emptyText="Tutte le stagioni" />
  </ReferenceInput>,
];

const RoleChip = (record) => {
  const role = record?.role;
  if (!role) return null;
  return <Chip size="small" label={role} variant="outlined" />;
};

const CopyRosterButton = () => {
  const [open, setOpen] = useState(false);
  const [seasons, setSeasons] = useState([]);
  const [sourceId, setSourceId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [loading, setLoading] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  const authHeader = () => ({ Authorization: `Basic ${localStorage.getItem("basicAuth")}` });

  const openDialog = async () => {
    const res = await fetch("/api/admin/seasons", { headers: authHeader() });
    const data = await res.json();
    setSeasons(data);
    // di solito si copia DALLA stagione corrente (che ha già la rosa) VERSO
    // quella nuova appena creata, non il contrario
    const current = data.find((s) => s.is_current);
    const other = data.find((s) => !s.is_current);
    setSourceId(current ? String(current.id) : "");
    setTargetId(other ? String(other.id) : "");
    setOpen(true);
  };

  const doCopy = async () => {
    setLoading(true);
    const res = await fetch("/api/admin/roster/copy-from-season", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({
        source_season_id: Number(sourceId),
        target_season_id: Number(targetId),
      }),
    });
    setLoading(false);
    if (!res.ok) {
      notify("Errore nella copia della rosa", { type: "error" });
      return;
    }
    const data = await res.json();
    notify(
      `Copiati ${data.copied} giocatori${data.skipped ? ` (${data.skipped} già presenti, saltati)` : ""}`,
      { type: "success" }
    );
    setOpen(false);
    refresh();
  };

  return (
    <>
      <Button size="small" startIcon={<ContentCopyIcon />} onClick={openDialog}>
        Copia rosa da stagione precedente
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Copia rosa tra stagioni</DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Copia numero di maglia e ruolo di ogni giocatore dalla stagione di partenza a quella
            di arrivo. I giocatori già presenti nella stagione di arrivo non vengono toccati.
          </Typography>

          <FormControl fullWidth size="small">
            <InputLabel>Da</InputLabel>
            <Select label="Da" value={sourceId} onChange={(e) => setSourceId(e.target.value)}>
              {seasons.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>A</InputLabel>
            <Select label="A" value={targetId} onChange={(e) => setTargetId(e.target.value)}>
              {seasons.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Annulla</Button>
          <Button
            variant="contained"
            disableElevation
            disabled={!sourceId || !targetId || sourceId === targetId || loading}
            onClick={doCopy}
          >
            Copia
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const RosterListActions = () => (
  <TopToolbar>
    <CopyRosterButton />
    <CreateButton />
    <ExportButton />
  </TopToolbar>
);

export const RosterList = () => (
  <List
    filters={rosterFilters}
    perPage={50}
    sort={{ field: "number", order: "ASC" }}
    actions={<RosterListActions />}
    sx={{ "& .RaList-content": { marginTop: 1 } }}
  >
    <Datagrid
      rowClick="edit"
      bulkActionButtons={false}
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        "& .RaDatagrid-headerCell": { fontWeight: 800 },
        "& .RaDatagrid-rowCell": { py: 1.25 },
      }}
    >
      <NumberField source="number" label="N°" />
      <TextField source="player_name" label="Giocatore" />
      <ReferenceField source="season_id" reference="admin/seasons" label="Stagione">
        <TextField source="label" />
      </ReferenceField>
      <FunctionField label="Ruolo" render={(record) => RoleChip(record)} />
      <BooleanField source="is_active" label="Attivo" />
    </Datagrid>
  </List>
);

const RosterFormFields = ({ isCreate }) => (
  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, width: "100%" }}>
    <ReferenceInput source="player_id" reference="admin/players" label="Giocatore">
      <SelectInput
        optionText="name"
        disabled={!isCreate}
        validate={isCreate ? [required()] : undefined}
        fullWidth
      />
    </ReferenceInput>
    <ReferenceInput source="season_id" reference="admin/seasons" label="Stagione">
      <SelectInput
        optionText="label"
        disabled={!isCreate}
        validate={isCreate ? [required()] : undefined}
        fullWidth
      />
    </ReferenceInput>
    <NumberInput source="number" label="Numero maglia" fullWidth />
    <TextInput source="role" label="Ruolo (opzionale, sovrascrive l'anagrafico)" fullWidth />
    <BooleanInput source="is_active" label="Attivo" defaultValue={true} />
  </Box>
);

export const RosterEdit = () => (
  <Edit title="Modifica in rosa">
    <SimpleForm>
      <RosterFormFields isCreate={false} />
    </SimpleForm>
  </Edit>
);

export const RosterCreate = () => (
  <Create title="Aggiungi in rosa">
    <SimpleForm>
      <RosterFormFields isCreate={true} />
    </SimpleForm>
  </Create>
);
